import { useEffect, useRef, useState } from 'react';

interface UseFieldValuesOptions<T> {
	formId: number;
	fieldIds: number[];
	shouldFetch?: boolean;
	transform?: (value: string) => T;
	filter?: (value: T) => boolean;
}

/**
 * Get field values synchronously from the DOM.
 *
 * @param formId    Form ID to get fields from
 * @param fieldIds  Array of field IDs to get values from
 * @param transform Function to transform field values
 * @param filter    Function to filter transformed values
 * @return  Array of transformed and filtered values
 */
const getFieldValues = <T = string>(
	formId: number,
	fieldIds: number[],
	transform: (value: string) => T,
	filter: (value: T) => boolean
): T[] => {
	return fieldIds.reduce<T[]>((acc, id) => {
		// Try to find the field by ID (works for select, text, hidden inputs)
		const element = document.getElementById(`input_${formId}_${id}`);
		let field:
			| HTMLInputElement
			| HTMLSelectElement
			| HTMLTextAreaElement
			| null = null;

		// Validate it's actually a form element
		if (
			element &&
			(element instanceof HTMLInputElement ||
				element instanceof HTMLSelectElement ||
				element instanceof HTMLTextAreaElement)
		) {
			field = element;
		}

		// If not found, check if it's a radio button group
		if (!field) {
			const radioButtons = document.querySelectorAll(
				`input[name="input_${id}"][type="radio"]`
			) as NodeListOf<HTMLInputElement>;

			if (radioButtons.length > 0) {
				// Find the checked radio button
				const checkedRadio = Array.from(radioButtons).find(
					(radio) => radio.checked
				);
				if (checkedRadio) {
					field = checkedRadio;
				} else {
					// No radio checked, return empty
					return acc;
				}
			}
		}

		if (!field) {
			// eslint-disable-next-line no-console
			console.log(`Field not found: input_${formId}_${id}`);
			return acc;
		}

		const value = transform(field.value);
		if (filter(value)) {
			acc.push(value);
		}

		return acc;
	}, []);
};

/**
 * Hook to watch Gravity Forms field values.
 * Only triggers state update when values actually change and shouldFetch is true.
 *
 * @param options             Hook options
 * @param options.formId      Form ID to watch fields from
 * @param options.fieldIds    Array of field IDs to watch
 * @param options.shouldFetch Whether to start watching fields (default: false)
 * @param options.transform   Function to transform field values (default: identity)
 * @param options.filter      Function to filter transformed values (default: () => true)
 * @return  Array of current field values
 *
 * @example Single field
 * ```ts
 * const serviceId = useFieldValues({
 *   formId: 1,
 *   fieldIds: [4],
 *   transform: value => parseInt(value, 10),
 *   filter: value => value > 0
 * });
 * ```
 *
 * @example Multiple fields
 * ```ts
 * const resourceIds = useFieldValues({
 *   formId: 1,
 *   fieldIds: [4, 5, 6],
 *   transform: value => parseInt(value, 10),
 *   filter: value => value > 0,
 *   shouldFetch: !!selectedDate
 * });
 * ```
 */
export function useFieldValues<T = string>({
	formId,
	fieldIds,
	shouldFetch = false,
	transform = (value: string) => value as unknown as T,
	filter = () => true,
}: UseFieldValuesOptions<T>) {
	const [values, setValues] = useState<T[]>(() => {
		// Read values synchronously on first render if shouldFetch is true
		return shouldFetch
			? getFieldValues(formId, fieldIds, transform, filter)
			: [];
	});

	const prevValuesRef = useRef<string>('');
	const listenersRef = useRef<(() => void)[]>([]);

	// Set up change listeners after initial render
	useEffect(() => {
		if (!shouldFetch || formId <= 0 || fieldIds.length === 0) {
			return;
		}

		const handleChange = () => {
			const newValues = getFieldValues(
				formId,
				fieldIds,
				transform,
				filter
			);
			const newValuesString = JSON.stringify(newValues);

			// Only update if values changed
			if (newValuesString !== prevValuesRef.current) {
				prevValuesRef.current = newValuesString;
				setValues(newValues);
			}
		};

		// Clean up old listeners
		listenersRef.current.forEach((cleanup) => cleanup());
		listenersRef.current = [];

		// Set up new listeners
		fieldIds.forEach((fieldId) => {
			// Try to find field by ID first (select, text, hidden)
			const element = document.getElementById(
				`input_${formId}_${fieldId}`
			);

			// Ensure element is actually an input/select/textarea element
			const isFormElement =
				element &&
				(element instanceof HTMLInputElement ||
					element instanceof HTMLSelectElement ||
					element instanceof HTMLTextAreaElement);

			if (isFormElement) {
				const field = element as
					| HTMLInputElement
					| HTMLSelectElement
					| HTMLTextAreaElement;

				// For number inputs, listen to both 'input' and 'change' events
				// 'input' fires on every keystroke, 'change' fires on blur
				// This ensures immediate updates while typing
				if (
					field instanceof HTMLInputElement &&
					field.type === 'number'
				) {
					field.addEventListener('input', handleChange);
					field.addEventListener('change', handleChange);
					listenersRef.current.push(() => {
						field.removeEventListener('input', handleChange);
						field.removeEventListener('change', handleChange);
					});
				} else {
					// Standard field - add single listener
					field.addEventListener('change', handleChange);
					listenersRef.current.push(() =>
						field.removeEventListener('change', handleChange)
					);
				}
			} else {
				// Check if it's a radio button group
				const radioButtons = document.querySelectorAll(
					`input[name="input_${fieldId}"][type="radio"]`
				) as NodeListOf<HTMLInputElement>;

				if (radioButtons.length > 0) {
					// Add listener to each radio button
					radioButtons.forEach((radio) => {
						radio.addEventListener('change', handleChange);
						listenersRef.current.push(() =>
							radio.removeEventListener('change', handleChange)
						);
					});
				}
			}
		});

		return () => {
			listenersRef.current.forEach((cleanup) => cleanup());
			listenersRef.current = [];
		};
	}, [formId, fieldIds, shouldFetch, transform, filter]);

	return values;
}
